import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Banner, Card, CardBody, CardFoot, CardHead, KV, KVRow, PageHead, Stat } from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { post, useApi } from '../lib/api.ts'
import { gbp, pnl, pnlClass } from '../lib/format.ts'
import type { PositionsPayload } from '../../shared/types.ts'

/**
 * Two interactions from anywhere, by design.
 *
 * It is deliberately calm: the button exists on every screen and its mere presence must
 * not be alarming. What is *not* calm is the confirmation — this closes real exposure at
 * market, so it states exactly what will happen before it happens.
 */
export function KillSwitch() {
  usePageTitle('Kill switch')
  const navigate = useNavigate()
  const { data } = useApi<PositionsPayload>('/positions', 5000)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string | null>(null)

  const t = data?.totals
  const armed = typed.trim().toUpperCase() === 'FLATTEN'

  const fire = async () => {
    setBusy(true)
    try {
      await post('/risk/controls', { action: 'flatten', note: 'Kill switch' })
      setDone('Flatten requested. All unmatched orders are cancelled and open positions are being closed at market.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="content" style={{ maxWidth: 900 }}>
      <PageHead
        title="Kill switch"
        sub="Cancel every unmatched order and close every open position at market, then stop trading."
      />

      {done ? (
        <Banner tone="flatten" icon="■" title="Flatten and halt requested">
          {done} Trading stays stopped until you resume from the risk centre.
        </Banner>
      ) : (
        <Banner tone="degraded" title="This closes real exposure at market prices">
          Flattening crosses the spread on every open selection. That is the point — it is a stop, not an optimisation
          — but it means you will pay the spread and may realise a loss that is currently unrealised. Nothing here is
          reversible by pressing it again.
        </Banner>
      )}

      <div className="grid cols-3" style={{ marginBottom: 16 }}>
        <Card pad>
          <Stat label="Positions to close" value={t?.openMarkets ?? '—'} sub={`${t?.openPositions ?? 0} runner positions`} />
        </Card>
        <Card pad>
          <Stat label="Orders to cancel" value={t?.workingOrders ?? '—'} sub={`${gbp(t?.unmatchedSize ?? 0)} unmatched`} />
        </Card>
        <Card pad>
          <Stat
            label="Unrealised P&L that would be realised"
            tone={(t?.unrealised ?? 0) >= 0 ? 'pos' : 'neg'}
            value={<span className={pnlClass(t?.unrealised)}>{pnl(t?.unrealised ?? 0)}</span>}
            sub="Approximate — you cross the spread on the way out"
          />
        </Card>
      </div>

      <Card>
        <CardHead title="What will happen, in order" />
        <CardBody>
          <KV>
            <KVRow label="1. Halt" text>
              The gate stops accepting new intents immediately.
            </KVRow>
            <KVRow label="2. Cancel" text>
              Every unmatched order is pulled — {t?.workingOrders ?? 0} order{t?.workingOrders === 1 ? '' : 's'},{' '}
              {gbp(t?.unmatchedSize ?? 0)}.
            </KVRow>
            <KVRow label="3. Flatten" text>
              Each open selection is hedged to a flat P&amp;L at the best price available.
            </KVRow>
            <KVRow label="4. Stop" text>
              Trading stays stopped until you resume deliberately from the risk centre.
            </KVRow>
          </KV>

          {!done && (
            <>
              <hr className="hr" />
              <div className="field">
                <label htmlFor="ks-confirm">
                  Type <b className="mono">FLATTEN</b> to confirm
                </label>
                <input
                  id="ks-confirm"
                  className="input mono"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder="FLATTEN"
                  autoComplete="off"
                />
                <div className="help">
                  A typed confirmation, not a second click — this is the one control that must never fire by accident.
                </div>
              </div>
              <div className="row" style={{ gap: 10 }}>
                <button className="btn danger lg" disabled={!armed || busy} onClick={() => void fire()}>
                  {busy ? 'Flattening…' : 'Flatten everything and halt'}
                </button>
                <button className="btn outline lg" onClick={() => navigate(-1)}>
                  Cancel — go back
                </button>
              </div>
            </>
          )}
        </CardBody>
        <CardFoot>
          The engine's own <span className="mono">flatten</span> command is a separate process and works even when this
          one is wedged — which is exactly when you would want it.
        </CardFoot>
      </Card>
    </main>
  )
}
